/**
 * `<MapSearch/>` — the editorial ⌘K / Ctrl-K "find on the map" command palette
 * (B2.13). A genuinely-new Map capability: a lead (or member) hits ⌘K, a
 * top-centred overlay opens, they type, and a list of matching nodes appears —
 * each row shows the node's state chip, its title with the matched substring
 * highlighted, its breadcrumb path, and any assignee avatar. ↵ jumps to and
 * focuses the node on the canvas; ⌘↵ opens its detail drawer; esc / click-out
 * closes.
 *
 * Issue #1043 (Wave 5). This is a CORE Map capability (founder 2026-05-26: no
 * separate per-component toggle), but it ships as part of the editorial Map and
 * so is rendered ONLY when the theme is editorial. Concretely: `MapEditor`
 * mounts `<MapSearch/>` only when `useMapTheme().mode === "editorial"`, so with
 * the `task_editorial_ui` flag OFF (every team today) nothing here mounts — no
 * overlay, and crucially NO ⌘K key listener attaches, so prod keyboard
 * behaviour is byte-unchanged until the flip. At the flip ⌘K becomes available
 * to everyone with no extra toggle.
 *
 * Search itself is pure + in-memory over the already-loaded forest
 * (`searchModel.searchMap`) — no fetch, no new endpoint. Selection + drawer go
 * through the existing actions the editor already passes (`treeStore`'s
 * `select` / `openNode`), so the palette reuses the one shared UI state.
 *
 * Listener scope (so ⌘K never fights the existing outline/canvas keys):
 *   - The global `keydown` listener is attached ONLY while this component is
 *     mounted (editorial only), and it acts ONLY on ⌘K / Ctrl-K to TOGGLE the
 *     palette — it never touches arrows / Enter / Tab, which the outline editor
 *     and canvas own. When the palette is open, navigation keys (↑↓ ↵ ⌘↵ esc)
 *     are handled on the palette's own input (`onKeyDown`), not globally, so
 *     they can't leak to the canvas.
 *   - It ignores ⌘K when an editor text field already has focus AND a meaningful
 *     modifier combo would clash — but ⌘K is otherwise free in this app, so we
 *     open on it regardless of focus, which is the platform-standard behaviour.
 */

import { useEffect, useId, useMemo, useRef, useState } from "react";

import type { TaskMember } from "../members/useTaskTeamMembers";
import { type MapSearchResult, searchMap } from "../search/searchModel";
import { useMapTheme } from "../theme/MapThemeContext";
import type { TreeNode } from "../tree/buildTree";
import { AvatarRow } from "./Avatar";
import { StatusChip } from "./StatusChip";

export interface MapSearchProps {
  /** The loaded forest to search (the same `buildTree` result the editor holds). */
  roots: TreeNode[];
  /** Team roster, for resolving assignee avatars on a result row. */
  members: TaskMember[];
  /**
   * Jump to + focus a node on the canvas (↵). Wired to `treeStore.select` by
   * the editor, so the selection the canvas + outline already mirror updates.
   */
  onJump: (id: string) => void;
  /**
   * Open a node's detail drawer (⌘↵). Wired to `treeStore.openNode` by the
   * editor (which also focuses the node).
   */
  onOpenDrawer: (id: string) => void;
}

/** True when `⌘K` (mac) or `Ctrl-K` (others) is the pressed combo. */
function isFindCombo(e: KeyboardEvent): boolean {
  return (e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K");
}

export function MapSearch({ roots, members, onJump, onOpenDrawer }: MapSearchProps) {
  const theme = useMapTheme();
  const c = theme.palette;
  // ⌘K is part of the editorial Map (founder 2026-05-26: core capability, no
  // separate toggle, but only in the editorial look). The editor already mounts
  // this only in editorial mode; this is the defence-in-depth so the component
  // is inert (no listener, no palette) under the legacy theme regardless of
  // caller — prod (flag off) is byte-unchanged.
  const editorial = theme.mode === "editorial";

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);

  const inputRef = useRef<HTMLInputElement>(null);
  const listboxId = useId();

  // Pure, in-memory match over the loaded forest. Recomputed only when the
  // query or the tree changes (typing is the common case).
  const results = useMemo(() => searchMap(roots, query), [roots, query]);

  // Member lookup for the per-row avatar (display-only, never a metric — §A9).
  const memberById = useMemo(() => new Map(members.map((m) => [m.membershipId, m])), [members]);

  // The global ⌘K / Ctrl-K toggle. Attached only while THIS component is
  // mounted (editorial only — see file header), so prod (flag off, this not
  // mounted) keeps its exact keyboard behaviour. It acts ONLY on the find combo
  // — never arrows / Enter / Tab — so it can't interfere with the outline
  // editor's or the canvas's own keys.
  useEffect(() => {
    if (!editorial) return;
    const onKey = (e: KeyboardEvent) => {
      if (!isFindCombo(e)) return;
      e.preventDefault();
      setOpen((prev) => !prev);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editorial]);

  // On open: reset the active row, clear nothing (keep the last query — a quick
  // re-find), and focus + select the input so the user types immediately
  // (focus-trap entry point). On close we leave the query as-is for next time.
  useEffect(() => {
    if (!open) return;
    setActiveIndex(0);
    // Defer focus to after the overlay paints.
    const id = window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
    return () => window.clearTimeout(id);
  }, [open]);

  // Keep the active row in range as results shrink while typing.
  useEffect(() => {
    setActiveIndex((i) => (results.length === 0 ? 0 : Math.min(i, results.length - 1)));
  }, [results.length]);

  if (!editorial || !open) return null;

  const close = () => setOpen(false);

  const jump = (result: MapSearchResult) => {
    onJump(result.id);
    close();
  };
  const openDrawer = (result: MapSearchResult) => {
    onOpenDrawer(result.id);
    close();
  };

  // Navigation lives on the input's own onKeyDown (NOT the global listener), so
  // arrows / Enter never leak to the canvas while the palette is open.
  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      close();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (results.length > 0) setActiveIndex((i) => (i + 1) % results.length);
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (results.length > 0) setActiveIndex((i) => (i - 1 + results.length) % results.length);
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const result = results[activeIndex];
      if (!result) return;
      // ⌘↵ / Ctrl-↵ opens the drawer; plain ↵ jumps + focuses on the canvas.
      if (e.metaKey || e.ctrlKey) openDrawer(result);
      else jump(result);
    }
  };

  return (
    <>
      {/* Click-outside scrim — closes the palette. Calm, low-opacity warm wash. */}
      <button
        type="button"
        data-testid="map-search-scrim"
        aria-label="Close search"
        onClick={close}
        className="absolute inset-0 z-40 cursor-default"
        style={{ backgroundColor: c.ink, opacity: 0.18 }}
      />

      {/* The find overlay, top-centred (wireframe V_B213). */}
      <div
        data-testid="map-search"
        // biome-ignore lint/a11y/useSemanticElements: a native <dialog> needs imperative show/close + a top-layer it would steal from the canvas; this is a custom command palette whose open/close + focus are managed in React state, so the role is the correct ARIA shape here.
        role="dialog"
        aria-modal="true"
        aria-label="Find on the map"
        className="-translate-x-1/2 absolute top-6 left-1/2 z-50 w-[min(500px,calc(100%-2rem))] overflow-hidden"
        style={{
          backgroundColor: c.cardBg,
          border: `1px solid ${c.ink}`,
          borderRadius: theme.radii.lg,
          boxShadow: "0 16px 44px rgba(0,0,0,0.12)",
        }}
      >
        {/* Input row: ⌘ K mono prefix · input · N RESULTS count. */}
        <div
          className="flex items-center gap-2 px-3.5 py-2.5"
          style={{ borderBottom: `1px solid ${c.rule}` }}
        >
          <span
            aria-hidden
            data-testid="map-search-prefix"
            className="text-[13px]"
            style={{ fontFamily: theme.fonts.mono, color: c.muted }}
          >
            ⌘ K
          </span>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onInputKeyDown}
            placeholder="search the map…"
            data-testid="map-search-input"
            // The combobox owning the results listbox (ARIA activedescendant
            // pattern): focus stays here and ↑↓ move the active option below.
            role="combobox"
            aria-label="Search the map"
            aria-expanded={results.length > 0}
            aria-controls={listboxId}
            aria-activedescendant={
              results[activeIndex] ? `${listboxId}-opt-${activeIndex}` : undefined
            }
            className="flex-1 bg-transparent text-[15px] outline-none"
            style={{ color: c.ink, fontFamily: theme.fonts.sans }}
          />
          <span
            data-testid="map-search-count"
            className="shrink-0 text-[10.5px] uppercase tracking-wider"
            style={{ fontFamily: theme.fonts.mono, color: c.muted }}
          >
            {results.length} {results.length === 1 ? "result" : "results"}
          </span>
        </div>

        {/* Results listbox. */}
        {query.trim().length === 0 ? (
          <div
            data-testid="map-search-empty"
            className="px-3.5 py-4 text-[12px]"
            style={{ color: c.faint, fontStyle: "italic" }}
          >
            Type to find any task on the map by name.
          </div>
        ) : results.length === 0 ? (
          <div
            data-testid="map-search-noresults"
            className="px-3.5 py-4 text-[12px]"
            style={{ color: c.faint, fontStyle: "italic" }}
          >
            No tasks match “{query.trim()}”.
          </div>
        ) : (
          // The combobox listbox. Per the ARIA `aria-activedescendant` pattern,
          // focus stays on the input and the OPTIONS are not individually
          // tab-focusable (the input's arrow keys move the active descendant);
          // keyboard activation lives entirely on the input.
          // biome-ignore lint/a11y/useFocusableInteractive: activedescendant pattern — the combobox INPUT holds focus + drives selection; the listbox itself is intentionally not tab-focusable.
          <div
            id={listboxId}
            // biome-ignore lint/a11y/useSemanticElements: a <select> can't render this rich row (chip + highlighted title + breadcrumb + avatar); the activedescendant listbox pattern is the correct ARIA shape.
            role="listbox"
            aria-label="Search results"
          >
            {results.map((result, i) => {
              const active = i === activeIndex;
              const before = result.title.slice(0, result.matchStart);
              const hit = result.title.slice(
                result.matchStart,
                result.matchStart + result.matchLength,
              );
              const after = result.title.slice(result.matchStart + result.matchLength);
              const assignees = result.assigneeIds
                .map((id) => memberById.get(id))
                .filter((m): m is TaskMember => Boolean(m));
              const path = result.breadcrumb.join(" › ");
              return (
                // A rich option row in the activedescendant listbox: the click is
                // a pointer-only shortcut to the SAME actions the input's keys
                // drive (↑↓ / ↵ / ⌘↵), so the focusable/keyboard rules are
                // silenced here and the row is intentionally not tab-focusable.
                // biome-ignore lint/a11y/useFocusableInteractive: activedescendant pattern — focus stays on the input, options are not tab-focusable by design.
                // biome-ignore lint/a11y/useKeyWithClickEvents: keyboard nav + activation are handled on the palette input (↑↓ / ↵ / ⌘↵ via aria-activedescendant); the click is a pointer-only shortcut to the same actions.
                <div
                  key={result.id}
                  id={`${listboxId}-opt-${i}`}
                  // biome-ignore lint/a11y/useSemanticElements: see the listbox note above — a rich option row, not an <option>.
                  role="option"
                  aria-selected={active}
                  data-testid="map-search-result"
                  data-active={active || undefined}
                  onMouseEnter={() => setActiveIndex(i)}
                  onClick={(e) => (e.metaKey || e.ctrlKey ? openDrawer(result) : jump(result))}
                  className="flex cursor-pointer items-center gap-3 px-3.5 py-2.5"
                  style={{
                    borderBottom: `1px solid ${c.rule}`,
                    backgroundColor: active ? c.bgRecess : "transparent",
                  }}
                >
                  <StatusChip state={result.state} />
                  <div className="min-w-0 flex-1">
                    <div
                      className="truncate text-[13px]"
                      style={{ color: c.ink, fontFamily: theme.fonts.sans }}
                    >
                      {before}
                      <mark
                        data-testid="map-search-mark"
                        style={{ backgroundColor: "#FFF1A8", color: c.ink, padding: "0 1px" }}
                      >
                        {hit}
                      </mark>
                      {after}
                    </div>
                    {path && (
                      <div
                        data-testid="map-search-path"
                        className="mt-0.5 truncate text-[10.5px] tracking-wider"
                        style={{ fontFamily: theme.fonts.mono, color: c.muted }}
                      >
                        {path}
                      </div>
                    )}
                  </div>
                  {assignees.length > 0 && <AvatarRow members={assignees} size={20} />}
                  {active && (
                    <span
                      aria-hidden
                      data-testid="map-search-enter-hint"
                      className="shrink-0 text-[11px]"
                      style={{ fontFamily: theme.fonts.mono, color: c.muted }}
                    >
                      ↵
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Footer hint strip — Kbd-chip key cues (wireframe). */}
        <div
          data-testid="map-search-footer"
          className="flex items-center justify-between gap-2 px-3.5 py-2 text-[11px]"
          style={{ backgroundColor: c.bgRecess, color: c.muted }}
        >
          <span className="flex flex-wrap items-center gap-1.5">
            <SearchKbd>↑↓</SearchKbd> navigate <span style={{ color: c.faint }}>·</span>{" "}
            <SearchKbd>↵</SearchKbd> jump &amp; focus <span style={{ color: c.faint }}>·</span>{" "}
            <SearchKbd>⌘↵</SearchKbd> open drawer
          </span>
          <span className="flex shrink-0 items-center gap-1.5">
            <SearchKbd>esc</SearchKbd> close
          </span>
        </div>
      </div>
    </>
  );
}

/**
 * The editorial keycap chip for the footer hint strip — mirrors the outline
 * editor's `Kbd` look (mono, `c.rule2` hairline + 2px bottom-border "lip"). A
 * small local copy keeps the palette self-contained (the outline's `Kbd` is not
 * exported); both read the theme so they stay visually in sync.
 */
function SearchKbd({ children }: { children: React.ReactNode }) {
  const theme = useMapTheme();
  const c = theme.palette;
  return (
    <span
      data-testid="map-search-kbd"
      className="inline-block rounded text-[10.5px] leading-none"
      style={{
        fontFamily: theme.fonts.mono,
        padding: "1px 5px",
        border: `1px solid ${c.rule2}`,
        borderBottomWidth: 2,
        backgroundColor: c.cardBg,
        color: c.slate,
      }}
    >
      {children}
    </span>
  );
}
